import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { makeStyles } from '@material-ui/core';

import { useDispatch, useSelector } from 'react-redux';
import { setAreaCriteria } from '../../../../../redux/search_area/Actions';

import SelectFilter from '../../../../Common/UI/Filter/SelectFilter';
import MultiSelectFilter from '../../../../Common/UI/Filter/MultiSelectFilter';
import NumberFilter from '../../../../Common/UI/Filter/NumberFilter';
import DateFilter from '../../../../Common/UI/Filter/DateFilter';
import StringFilter from '../../../../Common/UI/Filter/StringFilter';
import MultiStringFilter from '../../../../Common/UI/Filter/MultiStringFilter';
import AreaFilter from '../../../../Common/UI/Filter/AreaFilter';
import SimpleHeading from '../SimpleHeading';

const propTypes = {
    reducerName: PropTypes.string.isRequired,
    reducerAreaKey: PropTypes.string.isRequired,
    layerId: PropTypes.number.isRequired,
    tag: PropTypes.string,
    heading: PropTypes.string,
    onHandleChange: PropTypes.func,
    onHandleClear: PropTypes.func
};

const defaultProps = {
    tag: null,
    heading: null,
    onHandleChange: null,
    onHandleClear: null
};

const useStyles = makeStyles((theme) => style(theme));

function Criteria ({ reducerName, reducerAreaKey, layerId, tag, heading, onHandleChange, onHandleClear }) {
    const classes = useStyles();
    const layer = useSelector(state => state.layers.layers.find(el => el.id === layerId));
    const fields = {
        ...(layer.filterConfig ? layer.filterConfig.fields : {})
    };
    const criteria = useSelector(state => state[reducerName][reducerAreaKey].criteria);
    const dispatch = useDispatch();
    const dispatchSetAreaCriteria = (criteria) => dispatch(setAreaCriteria(reducerName, criteria));

    function handleChange (branch) {
        const newTree = { ...criteria };
        const idx = newTree.children.findIndex(el => el.id === branch.id);
        if (idx === -1) {
            newTree.children.push(branch);
        } else {
            newTree.children[idx] = branch;
        }
        dispatchSetAreaCriteria(newTree);
        onHandleChange && onHandleChange(newTree);
    }

    function handleClear (id) {
        const idx = criteria.children.findIndex(el => el.id === id);
        // Date fields are a special case because of their internal state, on change is only called when a valid date comes through
        // While on clear is always called whenever the internal state changes just in case, meaning the idx may not exist, so this safely prevents any errors
        if (idx === -1) {
            return;
        }
        const newTree = { ...criteria };
        const newChildren = [...newTree.children];
        newChildren.splice(idx, 1);
        newTree.children = newChildren;
        dispatchSetAreaCriteria(newTree);
        onHandleClear && onHandleClear(newTree);
    }

    // Check if after the change, the field is valid
    function handleValidChange (newTree) {
        onHandleValidChange && onHandleValidChange(newTree);
    }

    function sort (firstEl, secondEl) {
        const order1 = fields[firstEl].order || 0;
        const order2 = fields[secondEl].order || 0;
        if (order1 < order2) return -1;
        if (order1 > order2) return 1;
        return 0;
    }

    function getFilter (key) {
        const field = fields[key];
        const isRequired = Boolean(fields[key]?.isRequired)        
        const branch = criteria.children.find(el => el.field === key) || {
            id: isRequired ? `required_${key}` : uuidv4(),
            type: 'rule',
            field: key,
            properties: {}
        };
                
        switch (field.type) {
            case 'select':
                return <SelectFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            case 'multiselect':
                return <MultiSelectFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            case 'integer':
                return <NumberFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            case 'date':
                return <DateFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            case 'string':
                return <StringFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            case 'multistring':
                return <MultiStringFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            case 'area':
                return <AreaFilter key={key} tree={branch} field={field} onChange={handleChange} onClear={handleClear} />;
            default:
                return <div key={branch.id}></div>;
        }
    }

    return (
        <div className={classes.root}>
            {
                heading &&
                <SimpleHeading
                    label={heading}
                />
            }
            { Object.keys(fields)
                .filter(key => !Boolean(tag) || (fields[key].tags && fields[key].tags.includes(tag)) )
                .sort(sort)
                .map(getFilter) }
        </div>
    ); 
}

const style = theme => ({
    root: {
        '&> *:not(:last-child)':{
            marginBottom: theme.constants.verticalMargin
        }
    }
});

Criteria.propTypes = propTypes;
Criteria.defaultProps = defaultProps;

export default Criteria;